export type Visibility = "Internal" | "Shared";
export type TaskStatus =
  | "Not started"
  | "In progress"
  | "Blocked"
  | "In review"
  | "Complete"
  | "Not applicable"
  | "Cancelled";
export type TaskHealth = "On track" | "At risk" | "Blocked" | "Overdue";
export type ProjectScheduleHealth = "On track" | "At risk" | "Off track";
export type UserKind = "internal" | "client";
export type ProjectRole =
  | "owner"
  | "internal_editor"
  | "internal_member"
  | "client_lead"
  | "client_member"
  | "observer";

export type RichTextMarkType = "bold" | "italic" | "underline" | "link";

export interface RichTextMark {
  type: RichTextMarkType;
  attrs?: {
    href?: string;
  };
}

export type RichTextNodeType =
  | "doc"
  | "paragraph"
  | "heading"
  | "text"
  | "hardBreak"
  | "bulletList"
  | "orderedList"
  | "listItem"
  | "table"
  | "tableRow"
  | "tableHeader"
  | "tableCell"
  | "mention"
  | "secureImage";

export interface RichTextNode {
  type: RichTextNodeType;
  text?: string;
  marks?: RichTextMark[];
  attrs?: {
    level?: 2 | 3;
    start?: number;
    colspan?: number;
    rowspan?: number;
    colwidth?: number[] | null;
    id?: string;
    label?: string;
    fileId?: string;
    alt?: string;
    state?: "checking" | "available" | "blocked";
    previewSrc?: string;
  };
  content?: RichTextNode[];
}

export interface RichTextDocument {
  version: 1;
  doc: RichTextNode;
}

export const STANDARD_PROJECT_SECTIONS = [
  "Project management",
  "Operations",
  "Technology",
  "Reporting",
  "Workforce management",
  "Recruiting",
  "Finance",
  "People and culture",
  "Training",
] as const;

export interface SessionUser {
  id: string;
  displayName: string;
  email: string;
  kind: UserKind;
  initials: string;
  photoUrl?: string;
  organization: string;
  role: string;
  canCreateProjects: boolean;
}

export interface NotificationPreferences {
  immediate: boolean;
  digest: "daily" | "weekly" | "off";
}

export interface WorkspaceSettings {
  workspaceName: string;
  defaultTimezone: string;
  supportEmail: string;
  emailSenderName: string;
  emailReplyTo: string;
  emailDeliveryMode: "disabled" | "emulator" | "ses";
  digestHour: number;
  invitationExpiryDays: number;
  version: number;
}

export interface EmailTemplatePreview {
  id:
    | "internal_welcome"
    | "project_invited"
    | "account_setup"
    | "invitation_review"
    | "project_added"
    | "comment_added"
    | "daily_digest"
    | "weekly_digest"
    | "test_email";
  name: string;
  description: string;
  trigger: string;
  audience: string;
  subject: string;
  text: string;
  html: string;
}

export interface Participant {
  id: string;
  name: string;
  initials: string;
  photoUrl?: string;
  email: string;
  organization: string;
  jobTitle?: string;
  role: string;
  kind: UserKind;
  state?: "active" | "pending";
  version: number;
}

export interface ProjectAccess {
  role: ProjectRole | "workspace_admin";
  canViewInternal: boolean;
  canEditPlan: boolean;
  canManageProject: boolean;
  canComment: boolean;
  canExportProject: boolean;
}

export interface ProjectSummary {
  id: string;
  code: string;
  name: string;
  client: string;
  clientOrganizationId?: string;
  clientOrganizationLogoDataUrl?: string;
  description: string;
  descriptionRichText?: RichTextDocument;
  ownerUpdate: string;
  ownerUpdateBy: string;
  ownerUpdateAt: string;
  startDate: string;
  targetEndDate: string;
  workingWeekdays: number[];
  holidays: string[];
  progress: number;
  health: ProjectScheduleHealth;
  status: "Active" | "On hold" | "Completed" | "Archived";
  nextMilestone: string;
  nextMilestoneDate: string;
  blockers: number;
  overdue: number;
  updatedAt: string;
  targetPolicy: "Hard boundary" | "Warn only";
  defaultVisibility: Visibility;
  scheduleVersion: number;
  version: number;
}

export interface MyWorkItem {
  id: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  title: string;
  status: TaskStatus;
  health: TaskHealth;
  dueDate: string;
  visibility: Visibility;
}

export interface Organization {
  id: string;
  name: string;
  website: string;
  logoUrl: string;
  logoDataUrl?: string;
  notes: string;
  status: "active";
  updatedAt: string;
  version: number;
}

export interface DirectoryPerson {
  id: string;
  directoryProfileId?: string;
  displayName: string;
  email: string;
  jobTitle: string;
  photoDataUrl?: string;
  actorClass: "internal" | "external";
  organizationId?: string;
  source: "corporate_team" | "platform_guest";
  platformState: "active" | "not_signed_in";
}

export interface DirectoryPeoplePage {
  items: DirectoryPerson[];
  total: number;
  nextOffset: number | null;
}

export interface DirectorySyncStatus {
  configured: boolean;
  groupName: string;
  schedule: string;
  state: "not_run" | "queued" | "running" | "retrying" | "completed" | "failed";
  activeProfileCount: number;
  inactiveProfileCount: number;
  lastSuccessfulRunAt: string;
  lastFailureAt: string;
  activeJobId: string;
  queuedJobId: string;
}

export interface ProjectInvitation {
  id: string;
  email: string;
  displayName: string;
  organizationId?: string;
  role: "client_lead" | "client_member" | "observer";
  state: "pending" | "revoked" | "accepted" | "expired";
  deliveryState:
    | "emulator_queued"
    | "queued"
    | "accepted"
    | "delivered"
    | "delayed"
    | "bounced"
    | "complained"
    | "failed"
    | "provider_required";
  accountSetupRequired: boolean;
  createdAt: string;
  expiresAt: string;
}

export interface SearchResult {
  id: string;
  type: "project" | "task" | "comment" | "file" | "risk";
  title: string;
  context: string;
  projectId: string;
  route: string;
}

export interface ExportJob {
  id: string;
  projectId: string;
  audience: "internal" | "shared";
  format: "doc" | "html" | "pdf" | "xls";
  state: "ready" | "queued" | "failed";
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  itemCount?: number;
  sizeBytes?: number;
}

export interface ProjectTemplate {
  id: string;
  name: string;
  description: string;
  version: number;
  createdAt: string;
  createdBy: string;
  sectionCount?: number;
  taskCount?: number;
}

export interface ProjectBrief {
  projectId: string;
  visibility: "Internal";
  content: RichTextDocument;
  version: number;
  updatedAt: string;
  updatedBy: string;
}

export interface ProjectSection {
  id: string;
  name: string;
  progress: number;
  collapsed?: boolean;
  visibility?: Visibility;
  orderKey?: string;
}

export interface ChecklistItem {
  id: string;
  label: string;
  complete: boolean;
}

export interface QuestionItem {
  id: string;
  label: string;
  type:
    | "Short text"
    | "Long text"
    | "Yes or no"
    | "Single select"
    | "Multi-select"
    | "Date"
    | "Number"
    | "URL"
    | "File request";
  required?: boolean;
  helperText?: string;
  options?: string[];
  answer?: string;
}

export interface TaskComment {
  id: string;
  author: string;
  initials: string;
  photoUrl?: string;
  createdAt: string;
  body: string;
  bodyRichText?: RichTextDocument;
  visibility: Visibility;
  deleted?: boolean;
  version?: number;
  replies?: TaskComment[];
}

export interface TaskFile {
  id: string;
  name: string;
  context: string;
  parentType: "project" | "task" | "comment" | "risk" | "response";
  parentId?: string;
  uploadedBy: string;
  updatedAt: string;
  size: string;
  type: string;
  visibility: Visibility;
  availabilityState: "Available" | "Uploading" | "Needs attention";
  version?: number;
}

export interface ProjectTask {
  id: string;
  projectId: string;
  sectionId: string;
  parentId?: string;
  level: number;
  type: "task" | "checklist" | "questionnaire" | "summary" | "milestone";
  title: string;
  description: string;
  descriptionRichText?: RichTextDocument;
  notes: string;
  notesRichText?: RichTextDocument;
  ownerId?: string;
  ownerName: string;
  ownerInitials: string;
  ownerPhotoUrl?: string;
  ownerOrganization: string;
  startDate: string;
  dueDate: string;
  baselineDueDate?: string;
  duration: number;
  status: TaskStatus;
  health: TaskHealth;
  visibility: Visibility;
  progress: number;
  predecessorIds: string[];
  commentCount: number;
  fileCount: number;
  version: number;
  blocker?: string;
  issueOwnerId?: string;
  issueOwnerNeeded?: boolean;
  reopenReason?: string;
  checklist: ChecklistItem[];
  questions: QuestionItem[];
  comments: TaskComment[];
}

export interface TaskImportPreview {
  previewId: string;
  projectVersion: number;
  scheduleVersion: number;
  visibility: Visibility;
  tasks: Array<{
    id: string;
    title: string;
    sectionId: string;
    sectionName: string;
    startDate: string;
    dueDate: string;
    durationDays: number;
  }>;
  newSections: Array<{
    id: string;
    name: string;
    visibility: "internal" | "shared";
  }>;
  expiresAt: string;
}

export interface BulkTaskEditChanges {
  ownerActorId?: string | null;
  sectionId?: string;
  startDate?: string;
  status?: TaskStatus;
}

export interface BulkTaskEditPreview {
  previewId: string;
  expectedScheduleVersion: number;
  selectedCount: number;
  affectedCount: number;
  selectedTasks: Array<{ id: string; title: string }>;
  excludedTasks: Array<{
    id: string;
    title: string;
    reason: "derived_task_collection";
  }>;
  scheduleImpacts: Array<{
    taskId: string;
    title: string;
    kind: string;
    direct: boolean;
    oldStartDate: string;
    newStartDate: string;
    oldDueDate: string;
    newDueDate: string;
  }>;
  changedFields: string[];
  expiresAt: string;
}

export interface RiskItem {
  id: string;
  title: string;
  kind: "Risk" | "Issue";
  impact: "Low" | "Medium" | "High";
  owner: string;
  dueDate: string;
  state: "Open" | "Watching" | "Resolved";
  visibility: Visibility;
  linkedTask: string;
  mitigation: string;
}

export interface ActivityItem {
  id: string;
  actor: string;
  initials: string;
  photoUrl?: string;
  action: string;
  context: string;
  timestamp: string;
  visibility: Visibility;
}

export interface NotificationItem {
  id: string;
  title: string;
  body: string;
  projectName: string;
  timestamp: string;
  read: boolean;
  kind: "mention" | "schedule" | "assignment" | "risk";
  taskId?: string;
}

export interface ProjectBundle {
  project: ProjectSummary;
  sections: ProjectSection[];
  tasks: ProjectTask[];
  participants: Participant[];
  files: TaskFile[];
  risks: RiskItem[];
  activity: ActivityItem[];
}

export interface ScheduleImpact {
  taskId: string;
  taskTitle: string;
  oldStart: string;
  oldDue: string;
  newStart: string;
  newDue: string;
  affected: Array<{
    taskId: string;
    title: string;
    oldDue: string;
    newDue: string;
    owner: string;
  }>;
  crossesBoundary: boolean;
  targetEndDate: string;
  previewId?: string;
  expectedScheduleVersion?: number;
  canApply?: boolean;
  violationMessages?: string[];
  requiredTargetEndDate?: string;
}

export interface ProjectScheduleMovePreview {
  previewId: string;
  expectedProjectVersion: number;
  expectedScheduleVersion: number;
  oldProjectStartDate: string;
  proposedProjectStartDate: string;
  oldProjectTargetEndDate: string;
  proposedProjectTargetEndDate: string;
  weekdaysOnly: boolean;
  changes: Array<{
    taskId: string;
    kind: ProjectTask["type"];
    title: string;
    status: string;
    oldStartDate: string | null;
    oldDueDate: string | null;
    startDate: string | null;
    dueDate: string | null;
    durationDays: number | null;
  }>;
  violations: Array<{ code: string; taskId: string | null }>;
  warnings: Array<{ code: string; taskId: string | null }>;
  canApply: boolean;
  expiresAt: string;
}
